import { useMemo, useRef } from 'react';
import type { PublicShareAsset } from '@xphoto/shared';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { computeJustifiedLayout } from '../gallery/justified-layout';
import { useElementWidth } from '../gallery/use-element-width';
import { thumbnailUrl } from './api';

/**
 * Grille justifiée de la page publique.
 *
 * Réutilise `computeJustifiedLayout` — le module est PUR et, depuis ce lot, générique
 * sur `{ width, height }` : il n'a jamais eu besoin de plus, et le typer sur
 * `GalleryAsset` aurait obligé cette grille à fabriquer un faux asset portant chemin et
 * nom de fichier, c'est-à-dire à inventer précisément les champs que le DTO public
 * refuse de faire sortir.
 *
 * PAS de virtualisation, contrairement à `gallery/Grid.tsx` : un partage est un album
 * borné, pas une bibliothèque. `loading="lazy"` porte la charge (le navigateur ne
 * télécharge que ce qui approche du viewport). Voir `use-public-share.ts` pour l'arbitrage.
 */

/** Gouttière entre tuiles ET entre rangées (px) — resserrée : l'album prime sur le chrome. */
const GAP = 4;

/** Hauteur de rangée visée. Généreuse : la page publique est une vitrine, pas un outil. */
const TARGET_ROW_HEIGHT = 240;
const TARGET_ROW_HEIGHT_COMPACT = 150;

interface PublicShareGridProps {
  token: string;
  assets: readonly PublicShareAsset[];
  /** Ouvre le média au RANG donné (1-based, comme l'URL `/p/:token/:n`). */
  onOpen: (rank: number) => void;
}

export function PublicShareGrid({
  token,
  assets,
  onOpen,
}: PublicShareGridProps): React.JSX.Element {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref);

  // Sous ~640 px, des rangées de 240 px ne tiendraient qu'une ou deux tuiles : la
  // disposition justifiée n'aurait plus rien à justifier.
  const targetHeight = width > 0 && width < 640 ? TARGET_ROW_HEIGHT_COMPACT : TARGET_ROW_HEIGHT;

  const layout = useMemo(
    () => computeJustifiedLayout(assets, width, targetHeight, GAP),
    [assets, width, targetHeight],
  );

  return (
    <div ref={ref} className="flex flex-col" style={{ gap: `${GAP}px` }}>
      {layout.rows.map((row) => (
        <div key={row.index} className="flex" style={{ gap: `${GAP}px` }}>
          {row.tiles.map((tile, i) => {
            const asset = tile.asset;
            // Rang 1-based dans l'ensemble : c'est CE nombre qui est adressable dans
            // l'URL, et non l'identifiant de l'asset — un rang ne dit rien de la
            // bibliothèque, un uuid finirait par en dire.
            const rank = row.startIndex + i + 1;
            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => onOpen(rank)}
                aria-label={interpolate(t('publicShare.openMedia'), { n: rank })}
                className="group relative shrink-0 overflow-hidden rounded-sm bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                style={{ width: `${tile.width}px`, height: `${tile.height}px` }}
              >
                <img
                  src={thumbnailUrl(token, asset, 256)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width={Math.round(tile.width)}
                  height={Math.round(tile.height)}
                  className="h-full w-full object-cover transition-opacity duration-150 group-hover:opacity-90 motion-reduce:transition-none"
                />
                {asset.kind === 'video' && (
                  // L'information « c'est une vidéo » ne passe JAMAIS par la seule
                  // couleur ni par le seul survol (WCAG AA) : une icône, visible en
                  // permanence, sur un fond qui lui garantit son contraste.
                  <span className="pointer-events-none absolute bottom-1.5 end-1.5 rounded-full bg-black/55 p-1">
                    <PlayCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
